import { ValueProvider } from '@angular/core';
import { SkyproCommunicationConfig } from './const';
import {
  CommunicationApiResponse,
  Communication,
  MessageAuthor,
  Thread,
  CommunicationUserApiResponse,
  CommunicationContentRelated,
} from './interface';
import groupBy from 'lodash/groupBy';

export function mapCommunicationUserApiResponseToMessageAuthorById(
  users: CommunicationUserApiResponse[],
): Map<number, MessageAuthor> {
  return new Map(
    users.map(user => [
      user.id,
      {
        user: {
          id: user.id,
          name: user.name,
        },
        avatarUrl: user.avatarUrl,
        hasTutorIcon: user.type === 'tutor',
        type: user.type,
      },
    ]),
  );
}

export function mapApiResponseToCommunication(communicationApiResponse: CommunicationApiResponse): Communication {
  const usersById = mapCommunicationUserApiResponseToMessageAuthorById(communicationApiResponse.users);
  const messagesByThreadId = groupBy(communicationApiResponse.messages, 'threadId');

  return {
    id: communicationApiResponse.id,
    threads: communicationApiResponse.threads.map(thread => {
      const newThread: Thread = {
        id: thread.id,
        contentId: thread.contentId,
        author: usersById.get(thread.authorId)!,
        body: thread.text,
        attachments: thread.attachments,
        isEdited: thread.isEdited,
        isDeleted: thread.isDeleted,
        isPinned: thread.isPinned,
        isHidden: thread.isHidden,
        createdAt: thread.createdAt,
        status: thread.status,
        likeCount: thread.likeCount,
        isLikePressed: isPressedByContentId(communicationApiResponse.likes, thread.contentId),
        isDislikePressed: isPressedByContentId(communicationApiResponse.dislikes, thread.contentId),
        meta: thread.meta ?? null,
        messages: (messagesByThreadId[thread.id] ?? []).map(message => ({
          id: message.id,
          contentId: message.contentId,
          author: usersById.get(message.authorId)!,
          body: message.text,
          attachments: message.attachments,
          isEdited: message.isEdited,
          isDeleted: message.isDeleted,
          createdAt: message.createdAt,
          type: message.type,
          likeCount: message.likeCount,
          isLikePressed: isPressedByContentId(communicationApiResponse.likes, message.contentId),
          isDislikePressed: isPressedByContentId(communicationApiResponse.dislikes, message.contentId),
          meta: message.meta ?? null,
        })),
      };
      return newThread;
    }),
    threadsCount: communicationApiResponse.threadsCount,
  };
}

export function provideSkyproCommunicationConfig(config: Partial<SkyproCommunicationConfig>): ValueProvider {
  return { provide: SkyproCommunicationConfig, useValue: config };
}

// Заменяет элементы списков в html-строке плоским списком параграфов
export function convertHtmlListToParagraphs(htmlString: string): string {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  // Парсим HTML
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Заменяем все <ul> and <ol> и их вложенные <li> элементы на параграфы
  const listElements = doc.querySelectorAll('ul, ol');

  listElements.forEach(listElement => {
    const listItems = listElement.children;

    Array.from(listItems).forEach((listItem, index) => {
      const paragraph = doc.createElement('p');
      const prefix = `${listElement.tagName === 'OL' ? index + 1 + '.' : '•'}`;
      // eslint-disable-next-line functional/immutable-data
      paragraph.innerHTML = `&Tab;&Tab;${prefix} ${listItem.innerHTML}`;
      listItem.replaceWith(paragraph);
    });

    // делаем плоский список параграфов
    listElement.replaceWith(...Array.from(listElement.childNodes));
  });

  // Serialize the modified Document back to an HTML string
  return serializer.serializeToString(doc);
}

export function getThreadDeliveryStatus(
  threadCreatedAt: string,
  authorType: string,
  meta: object,
): 'pending' | 'delivered' | 'failed' {
  const deliveryStatus = meta['deliveryStatus'] ?? null;
  const deliveryStatusUpdatedAt = meta['deliveryStatusUpdatedAt'] ?? null;

  // Если автор не куратор, то считаем что сообщение доставлено
  if (authorType === 'student') {
    return 'delivered';
  }

  // если сообщение создано до раскатки трекинга статусов в meta, то считаем, что оно доставлено
  if (new Date(threadCreatedAt).getTime() < Date.UTC(2025, 3, 4, 14, 45, 0)) {
    return 'delivered';
  }

  if (!deliveryStatusUpdatedAt || !deliveryStatus) {
    return 'pending';
  }

  // возвращаем данные статусы как есть
  if (['delivered', 'failed'].includes(deliveryStatus)) {
    return deliveryStatus;
  }

  // если сообщение в статусе pending больше 1 минуты, то считаем, что оно не доставлено
  if (deliveryStatus === 'pending' && Date.now() - new Date(deliveryStatusUpdatedAt).getTime() > 1000 * 60) {
    return 'failed';
  }

  return 'pending';
}

function isPressedByContentId(likes: CommunicationContentRelated[], contentId: number): boolean {
  return likes.some(like => like.contentId === contentId);
}
